import React from 'react';
import {
  Banner,
  Brief,
  CondensedLabel,
  CondensedMedium,
  Gutter,
  Layout,
  ParagraphHeader,
  useApi,
  Well,
} from '@axiom/ui';
import { EventsConst } from '@axiom/const';
import type { Candidate, Opportunity } from '@axiom/validation';

import { OpportunityLegacyApi } from '../../api/opportunity-legacy-api';
import { TalentActivitiesApi } from '../../api/talent-activities-api';
import { TalentOpportunitiesApi } from '../../api/talent-opportunities-api';
import { TaxonomyApi } from '../../api/taxonomy-api';
import { CandidateApi } from '../../api/candidate-api';

import { OpportunityTalentMatchSummaryChecklist } from './OpportunityTalentMatchSummaryChecklist';
import { OpportunityTalentMatchSummaryFitMetrics } from './OpportunityTalentMatchSummaryFitMetrics';

export const OpportunityTalentMatchSummary = ({
  name = 'OPPORTUNITY_TALENT_MATCH_SUMMARY',
  opportunityId,
  candidateId,
  isBenchCandidate = false,
}: {
  name?: string;
  opportunityId: Opportunity['id'];
  candidateId: Candidate['id'];
  isBenchCandidate?: boolean;
}) => {
  const [
    { data: candidate },
    { data: opportunity },
    { data: opportunityCandidates },
    { data: candidateOpportunities },
    { data: taxonomy },
    { data: candidateEvents },
  ] = useApi(
    CandidateApi.readCandidate(candidateId),
    OpportunityLegacyApi.readOpportunity(opportunityId),
    OpportunityLegacyApi.readOpportunityCandidates(opportunityId),
    TalentOpportunitiesApi.readCandidateOpportunities(candidateId),
    TaxonomyApi.readTaxonomy(),
    TalentActivitiesApi.readActivities(candidateId)
  );

  const opportunityCandidate = opportunityCandidates.find(
    co => co.candidateId === candidateId
  );

  const talentSubmissionNote =
    [...candidateEvents]
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )
      .find(
        event =>
          event.purpose === EventsConst.Purposes.ExpressedInterest &&
          event.opportunityId === opportunityId
      )?.description ?? null;

  return (
    <Layout direction="vertical" verticalGutter="16px" name={name}>
      <OpportunityTalentMatchSummaryChecklist
        name="OPPORTUNITY_TALENT_MATCH_SUMMARY_CHECKLIST"
        candidate={candidate}
        opportunity={opportunity}
        opportunityCandidate={opportunityCandidate}
        candidateOpportunities={candidateOpportunities}
        taxonomy={taxonomy}
      />
      <Well name="talent-summary-bio-well">
        <Gutter bottom="8px">
          <ParagraphHeader>Talent Summary Bio</ParagraphHeader>
        </Gutter>
        <Brief briefMaxHeight="120px" pattern="secondary">
          {(candidate.candidateSummary?.length ?? 0) > 0 ? (
            <CondensedMedium name="talent-summary">
              {candidate.candidateSummary}
            </CondensedMedium>
          ) : (
            <CondensedLabel name="no-talent-summary">
              Talent has no summary bio
            </CondensedLabel>
          )}
        </Brief>
        {!!talentSubmissionNote && (
          <Gutter top="16px" name="talent-submission-note-section">
            <Gutter bottom="8px">
              <ParagraphHeader>Talent Submission Note</ParagraphHeader>
            </Gutter>
            <Brief briefMaxHeight="20px" pattern="secondary">
              <CondensedMedium name="talent-submission-note">
                {talentSubmissionNote}
              </CondensedMedium>
            </Brief>
          </Gutter>
        )}
      </Well>
      {isBenchCandidate ? (
        <Banner type="info" name="bench-candidate-match-summary">
          Add this talent to a position to load AI summary data.
        </Banner>
      ) : (
        <OpportunityTalentMatchSummaryFitMetrics
          name="MATCH_SUMMARY"
          opportunityId={opportunityId}
          candidateId={candidateId}
        />
      )}
    </Layout>
  );
};
